import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import MetaTags from 'react-meta-tags';
import jwt_decode from 'jwt-decode';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import { Input, Spinner } from 'reactstrap';
import { Paper, Tooltip, Typography } from '@mui/material';
import FeatherIcon from 'feather-icons-react';
import Breadcrumbs from '../../../components/Common/Breadcrumb';
import { getPropertyList } from '../../Inventory/pages/FacilityInventory/Helpers/Api';
import {
  getAssets,
  deleteAsset,
  updateVehicleKm,
  getAssetTasks,
  deleteTask,
  reopenTask,
  uploadTaskFiles,
  addTaskFile,
  openTaskFile,
  getSuggestions,
} from './Api';
import { AssetModal, TaskModal, CompleteModal } from './ServiceModals';
import AutomatedEmailsSettings from '../AutomatedEmails/AutomatedEmailsSettings';
import '../InspectionTracker/Generate/Components/inspection-tracker.scss';
import '../InspectionTracker/Generate/Components/tracker-status.scss';
import './service-tracker.scss';

// Vehicle and equipment service trackers share this page: `kind` picks the
// columns and the fields. Rows are coloured by their worst open service.

const COPY = {
  vehicle: { title: 'Vehicle Services', one: 'vehicle', add: 'Add vehicle', empty: 'No vehicles yet', icon: 'truck' },
  equipment: { title: 'Equipment Services', one: 'equipment', add: 'Add equipment', empty: 'No equipment here yet', icon: 'tool' },
};

const TASK_STATE = {
  overdue: { icon: 'alert-circle', word: 'Overdue' },
  due_soon: { icon: 'clock', word: 'Due soon' },
  scheduled: { icon: 'calendar', word: 'Scheduled' },
};

const ROW_STATUS = {
  overdue: { icon: 'alert-circle', word: 'overdue' },
  due_soon: { icon: 'clock', word: 'due soon' },
  done: { icon: 'check-circle', word: 'up to date' },
  none: { icon: 'calendar', word: 'nothing due' },
};

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' }) : '—');
const fmtKm = (n) => (n === null || n === undefined ? '—' : Number(n).toLocaleString('en-US'));
const fileName = (key) => String(key || '').split('/').pop().replace(/^\d+_/, '');
const relativeDue = (days) => {
  if (typeof days !== 'number') return '';
  if (days === 0) return 'today';
  if (days < 0) return `${Math.abs(days)} day${days === -1 ? '' : 's'} late`;
  return `in ${days} day${days === 1 ? '' : 's'}`;
};

const FileButtons = ({ files = [] }) =>
  files.length ? (
    files.map((key) => (
      <button key={key} type="button" className="st-file" onClick={() => openTaskFile(key)} aria-label={`Open ${fileName(key)}`}>
        <FeatherIcon icon={/\.pdf$/i.test(key) ? 'file-text' : 'image'} size={12} /> {fileName(key).slice(0, 22)}
      </button>
    ))
  ) : (
    <span className="ts-secondary">—</span>
  );

const OpenTask = ({ task, canRecord, canManage, onComplete, onEdit, onRemove }) => {
  const state = TASK_STATE[task.status] || TASK_STATE.scheduled;
  return (
    <div className="st-task">
      <div className="st-task__body">
        <div className="st-task__title">{task.title}</div>
        <div className="st-task__due">
          <span className={`st-task__state st-task__state--${task.status}`}><FeatherIcon icon={state.icon} size={12} /> {state.word}</span>
          {task.dueDate && <span>{fmtDate(task.dueDate)} · {relativeDue(task.daysUntilDue)}</span>}
          {task.dueKm != null && (
            <span>
              at {fmtKm(task.dueKm)} km
              {typeof task.kmLeft === 'number' ? ` · ${task.kmLeft > 0 ? `${fmtKm(task.kmLeft)} km left` : `${fmtKm(Math.abs(task.kmLeft))} km over`}` : ''}
            </span>
          )}
          {task.repeatLabel && <span><FeatherIcon icon="repeat" size={11} /> {task.repeatLabel}</span>}
        </div>
      </div>
      <div className="st-task__actions">
        {canRecord && (
          <button type="button" className="ts-icon-btn ts-icon-btn--primary" onClick={onComplete} aria-label={`Complete ${task.title}`}>
            <FeatherIcon icon="check" size={14} /> Complete
          </button>
        )}
        {canManage && (
          <>
            <button type="button" className="ts-icon-btn" onClick={onEdit} aria-label={`Edit ${task.title}`}><FeatherIcon icon="edit-2" size={13} /></button>
            <button type="button" className="ts-icon-btn ts-icon-btn--danger" onClick={onRemove} aria-label={`Remove ${task.title}`}><FeatherIcon icon="x" size={14} /></button>
          </>
        )}
      </div>
    </div>
  );
};

// The asset's whole service history, loaded when its row is opened.
const History = ({ asset, isVehicle, canManage, canRecord, refreshKey, onChanged }) => {
  const [tasks, setTasks] = useState(null);
  const fileInput = useRef(null);
  const targetTask = useRef(null);

  const load = useCallback(async () => {
    try {
      setTasks(await getAssetTasks(asset._id));
    } catch {
      setTasks([]);
    }
  }, [asset._id]);
  useEffect(() => { load(); }, [load, refreshKey]);

  const reopen = async (task) => {
    const answer = await Swal.fire({
      title: 'Reopen this service?',
      text: 'The completion is cleared. If it scheduled the next service, that one is removed.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#DC2626',
      confirmButtonText: 'Reopen',
      reverseButtons: true,
    });
    if (!answer.isConfirmed) return;
    try {
      await reopenTask(task._id);
      toast.success('Reopened');
      onChanged();
    } catch {
      // the Api layer has shown the reason
    }
  };

  const pickFile = (task) => {
    targetTask.current = task;
    fileInput.current?.click();
  };
  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    const task = targetTask.current;
    if (!files.length || !task) return;
    try {
      const keys = await uploadTaskFiles(task._id, files);
      for (const key of keys) await addTaskFile(task._id, key);
      if (keys.length) toast.success(`${keys.length} file${keys.length === 1 ? '' : 's'} attached`);
      load();
      onChanged();
    } catch {
      // toast already shown
    }
  };

  if (tasks === null) return <div className="st-history"><Spinner size="sm" /> Loading history…</div>;
  const completed = tasks.filter((t) => t.status === 'completed');
  return (
    <div className="st-history">
      <input ref={fileInput} type="file" multiple accept="application/pdf,image/*" hidden onChange={onFiles} />
      {completed.length === 0 ? (
        <span className="ts-secondary">No completed services yet. Completed services are listed here with their notes and invoices.</span>
      ) : (
        <table>
          <thead>
            <tr>
              <th scope="col">Service</th>
              <th scope="col">Needed by</th>
              <th scope="col">Completed</th>
              {isVehicle && <th scope="col">Km</th>}
              <th scope="col">Notes</th>
              <th scope="col">Invoice / photos</th>
              <th scope="col"><span className="visually-hidden">Actions</span></th>
            </tr>
          </thead>
          <tbody>
            {completed.map((t) => (
              <tr key={t._id}>
                <td className="ts-primary">{t.title}</td>
                <td>{t.dueDate ? fmtDate(t.dueDate) : t.dueKm != null ? `${fmtKm(t.dueKm)} km` : '—'}</td>
                <td>
                  {fmtDate(t.completion?.completedDate)}
                  {t.completion?.completedBy?.displayName && <div className="ts-secondary">by {t.completion.completedBy.displayName}</div>}
                </td>
                {isVehicle && <td>{fmtKm(t.completion?.km)}</td>}
                <td style={{ maxWidth: 280, whiteSpace: 'pre-wrap' }}>{t.completion?.notes || '—'}</td>
                <td><FileButtons files={t.completion?.files} /></td>
                <td>
                  <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                    {canRecord && (
                      <Tooltip title="Attach an invoice or photo" arrow>
                        <button type="button" className="ts-icon-btn" onClick={() => pickFile(t)} aria-label={`Attach a file to ${t.title}`}><FeatherIcon icon="paperclip" size={13} /></button>
                      </Tooltip>
                    )}
                    {canManage && (
                      <Tooltip title="Reopen" arrow>
                        <button type="button" className="ts-icon-btn" onClick={() => reopen(t)} aria-label={`Reopen ${t.title}`}><FeatherIcon icon="rotate-ccw" size={13} /></button>
                      </Tooltip>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

const ServiceTracker = ({ kind }) => {
  const copy = COPY[kind];
  const isVehicle = kind === 'vehicle';
  const currentYear = new Date().getFullYear();
  const [properties, setProperties] = useState([]);
  const [filters, setFilters] = useState({ property_id: '', building_name: '', year: currentYear });
  const [rows, setRows] = useState([]);
  const [perms, setPerms] = useState({ canManage: false, canRecord: false });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);
  const [suggestions, setSuggestions] = useState({ types: [], titles: [] });
  const [assetModal, setAssetModal] = useState({ open: false, asset: null });
  const [taskModal, setTaskModal] = useState({ open: false, asset: null, task: null });
  const [completeModal, setCompleteModal] = useState({ open: false, asset: null, task: null });
  const [showReminders, setShowReminders] = useState(false);
  // Reminder settings belong to the company login; auditors may look.
  const role = useMemo(() => {
    try {
      const token = localStorage.getItem('accessToken');
      return token ? String(jwt_decode(token)?.role || '') : '';
    } catch {
      return '';
    }
  }, []);
  const canSeeReminders = role === 'company' || role === 'auditor';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const out = await getAssets({
        kind,
        propertyId: !isVehicle && filters.property_id ? filters.property_id : undefined,
        buildingName: !isVehicle && filters.building_name ? filters.building_name : undefined,
        year: isVehicle ? undefined : filters.year,
      });
      setRows(out?.data || []);
      setPerms({ canManage: !!out?.canManage, canRecord: !!out?.canRecord });
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [kind, isVehicle, filters.property_id, filters.building_name, filters.year]);

  const changed = useCallback(() => {
    load();
    setRefreshKey((k) => k + 1);
    getSuggestions(kind).then(setSuggestions);
  }, [load, kind]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { getSuggestions(kind).then(setSuggestions); }, [kind]);
  useEffect(() => {
    if (isVehicle) return;
    try {
      const token = localStorage.getItem('accessToken');
      const user = token ? jwt_decode(token) : null;
      if (!user) return;
      getPropertyList(user.role || '', user.id || '').then((result) => setProperties(result?.propertyList || [])).catch(() => setProperties([]));
    } catch {
      setProperties([]);
    }
  }, [isVehicle]);

  const buildings = useMemo(
    () => (properties.find((p) => p._id === filters.property_id)?.building || []).filter((b) => b?.buildingName),
    [properties, filters.property_id]
  );
  const singleBuilding = !isVehicle && !!filters.property_id && !!filters.building_name;

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((a) =>
      [a.vehicle?.plateNumber, a.vehicle?.vehicleType, a.vehicle?.model, a.equipment?.equipmentType, a.equipment?.name, a.equipment?.buildingName, a.propertyName, a.notes, ...(a.openTasks || []).map((t) => t.title)]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    );
  }, [rows, search]);

  const counts = useMemo(() => {
    const c = { overdue: 0, due_soon: 0, done: 0, none: 0 };
    visible.forEach((r) => { c[r.rowStatus] = (c[r.rowStatus] || 0) + 1; });
    return c;
  }, [visible]);

  const editKm = async (asset) => {
    const answer = await Swal.fire({
      title: `Km for ${asset.vehicle.plateNumber}`,
      input: 'number',
      inputValue: asset.vehicle.currentKm ?? '',
      inputAttributes: { min: 0, step: 1, 'aria-label': 'Current km' },
      inputValidator: (v) => (v === '' || Number(v) < 0 ? 'Enter the current km' : undefined),
      showCancelButton: true,
      confirmButtonColor: '#1A6B4A',
      confirmButtonText: 'Save',
      reverseButtons: true,
    });
    if (!answer.isConfirmed) return;
    const km = Number(answer.value);
    try {
      try {
        await updateVehicleKm(asset._id, km);
      } catch (e) {
        if (!e.lowerKm) throw e;
        const sure = await Swal.fire({ title: 'Lower km than recorded', text: e.message, icon: 'question', showCancelButton: true, confirmButtonColor: '#1A6B4A', confirmButtonText: 'Save anyway', cancelButtonText: 'Back', reverseButtons: true });
        if (!sure.isConfirmed) return;
        await updateVehicleKm(asset._id, km, true);
      }
      toast.success('Km updated');
      changed();
    } catch {
      // the Api layer has shown the reason
    }
  };

  const removeAsset = async (asset) => {
    const name = isVehicle ? asset.vehicle.plateNumber : [asset.equipment.equipmentType, asset.equipment.name].filter(Boolean).join(' · ');
    const answer = await Swal.fire({
      title: `Remove ${name}?`,
      text: 'If it has service history it is archived, so the history is kept. Otherwise it is deleted.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#DC2626',
      confirmButtonText: 'Remove',
      reverseButtons: true,
    });
    if (!answer.isConfirmed) return;
    try {
      const out = await deleteAsset(asset._id);
      toast.success(out?.message || 'Removed');
      changed();
    } catch {
      // toast already shown
    }
  };

  const removeTask = async (task) => {
    const answer = await Swal.fire({ title: `Remove "${task.title}"?`, text: 'This open service is removed. Completed services stay in the history.', icon: 'warning', showCancelButton: true, confirmButtonColor: '#DC2626', confirmButtonText: 'Remove', reverseButtons: true });
    if (!answer.isConfirmed) return;
    try {
      await deleteTask(task._id);
      toast.success('Service removed');
      changed();
    } catch {
      // toast already shown
    }
  };

  const columnCount = isVehicle ? 8 : singleBuilding ? 7 : 8;
  const last = (asset) => asset.lastCompleted;

  return (
    <div className="page-content">
      <MetaTags><title>Rentdigicare | {copy.title}</title></MetaTags>
      <div className="container-fluid">
        <Breadcrumbs title="Maintenance" breadcrumbItem={copy.title} />

        <div className="st-toolbar">
          {!isVehicle && (
            <>
              <div className="st-field">
                <label htmlFor="st-f-prop">Property</label>
                <Input id="st-f-prop" type="select" value={filters.property_id} onChange={(e) => setFilters((f) => ({ ...f, property_id: e.target.value, building_name: '' }))}>
                  <option value="">All properties</option>
                  {properties.map((p) => <option key={p._id} value={p._id}>{p.title || p.name}</option>)}
                </Input>
              </div>
              <div className="st-field">
                <label htmlFor="st-f-bldg">Building</label>
                <Input id="st-f-bldg" type="select" value={filters.building_name} onChange={(e) => setFilters((f) => ({ ...f, building_name: e.target.value }))} disabled={!buildings.length}>
                  <option value="">All buildings</option>
                  {buildings.map((b) => <option key={b.buildingName} value={b.buildingName}>{b.buildingName}</option>)}
                </Input>
              </div>
              <div className="st-field st-field--year">
                <label htmlFor="st-f-year">Year</label>
                <Input id="st-f-year" type="select" value={filters.year} onChange={(e) => setFilters((f) => ({ ...f, year: Number(e.target.value) }))}>
                  {[currentYear - 2, currentYear - 1, currentYear, currentYear + 1].map((y) => <option key={y} value={y}>{y}</option>)}
                </Input>
              </div>
            </>
          )}
          <div className="st-field st-field--search">
            <label htmlFor="st-f-search">Search</label>
            <Input id="st-f-search" type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder={isVehicle ? 'Plate, type, model or service' : 'Equipment, building or service'} />
          </div>
        </div>

        <Paper elevation={0} className="it-wrap it-paper">
          <div className="it-header">
            <div className="it-header__left">
              <div className="it-header__icon-wrap"><FeatherIcon icon={copy.icon} size={18} color="#A7F3D0" /></div>
              <div className="it-header__meta">
                <div className="it-header__label">{copy.title}{isVehicle ? '' : ` · ${filters.year}`}</div>
                <div className="it-header__title">{visible.length} {isVehicle ? `vehicle${visible.length === 1 ? '' : 's'}` : `item${visible.length === 1 ? '' : 's'}`}</div>
              </div>
              <div className="it-header__badges">
                {['overdue', 'due_soon', 'done', 'none'].filter((s) => counts[s] > 0).map((s) => (
                  <span key={s} className={`ts-status-pill ts-status-pill--${s}`}><FeatherIcon icon={ROW_STATUS[s].icon} size={12} /> {counts[s]} {ROW_STATUS[s].word}</span>
                ))}
              </div>
            </div>
            <div className="it-header__actions">
              {canSeeReminders && (
                <button type="button" className="it-btn it-btn--ghost-light" onClick={() => setShowReminders((v) => !v)} aria-expanded={showReminders}>
                  <FeatherIcon icon="bell" size={14} /> {showReminders ? 'Hide reminders' : 'Reminders'}
                </button>
              )}
              {perms.canManage && (
                <button type="button" className="it-btn it-btn--primary-solid it-add" onClick={() => setAssetModal({ open: true, asset: null })}>
                  <FeatherIcon icon="plus" size={14} /> {copy.add}
                </button>
              )}
            </div>
          </div>

          <div className="it-scroll-box" style={{ maxHeight: '72vh', overflow: 'auto', borderRadius: '0 0 10px 10px' }}>
            <table className="ts-table">
              <thead>
                {isVehicle ? (
                  <tr>
                    <th scope="col">Plate</th>
                    <th scope="col">Type</th>
                    <th scope="col">Model</th>
                    <th scope="col">Km</th>
                    <th scope="col">Service needed</th>
                    <th scope="col">Last completed</th>
                    <th scope="col">Invoice</th>
                    <th scope="col"><span className="visually-hidden">Actions</span></th>
                  </tr>
                ) : (
                  <tr>
                    {!singleBuilding && <th scope="col">Building</th>}
                    <th scope="col">Type of equipment</th>
                    <th scope="col">Name</th>
                    <th scope="col">Service needed</th>
                    <th scope="col">Completion date</th>
                    <th scope="col">Notes</th>
                    <th scope="col">Invoice</th>
                    <th scope="col"><span className="visually-hidden">Actions</span></th>
                  </tr>
                )}
              </thead>
              <tbody>
                {visible.length === 0 && (
                  <tr>
                    <td colSpan={columnCount} className="it-empty-cell">
                      <div className="it-empty">
                        <div className="it-empty__icon-wrap">{loading ? <Spinner size="sm" /> : <FeatherIcon icon={copy.icon} size={26} color="#1A6B4A" />}</div>
                        <Typography className="it-empty__title">{loading ? 'Loading…' : search ? 'Nothing matches the search' : copy.empty}</Typography>
                        {!loading && !search && (
                          <Typography className="it-empty__sub">
                            {perms.canManage ? `Click "${copy.add}", then add the services it needs.` : 'Nothing has been set up here yet.'}
                          </Typography>
                        )}
                      </div>
                    </td>
                  </tr>
                )}

                {visible.map((asset) => {
                  const open = expanded === asset._id;
                  const done = last(asset);
                  const services = (
                    <td style={{ minWidth: 320 }}>
                      {asset.openTasks.length === 0 ? (
                        <span className="ts-secondary">Nothing scheduled{isVehicle ? '' : ` in ${filters.year}`}</span>
                      ) : (
                        asset.openTasks.map((t) => (
                          <OpenTask
                            key={t._id}
                            task={t}
                            canRecord={perms.canRecord}
                            canManage={perms.canManage}
                            onComplete={() => setCompleteModal({ open: true, asset, task: t })}
                            onEdit={() => setTaskModal({ open: true, asset, task: t })}
                            onRemove={() => removeTask(t)}
                          />
                        ))
                      )}
                    </td>
                  );
                  const actions = (
                    <td>
                      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                        {perms.canManage && (
                          <Tooltip title="Add a service" arrow>
                            <button type="button" className="ts-icon-btn" onClick={() => setTaskModal({ open: true, asset, task: null })} aria-label="Add a service"><FeatherIcon icon="plus" size={14} /> Service</button>
                          </Tooltip>
                        )}
                        <Tooltip title={open ? 'Hide history' : 'Service history'} arrow>
                          <button type="button" className="ts-icon-btn" onClick={() => setExpanded(open ? '' : asset._id)} aria-expanded={open} aria-label="Service history">
                            <FeatherIcon icon="clock" size={14} />{asset.completedCount > 0 ? ` ${asset.completedCount}` : ''}
                          </button>
                        </Tooltip>
                        {perms.canManage && (
                          <>
                            <Tooltip title="Edit" arrow>
                              <button type="button" className="ts-icon-btn" onClick={() => setAssetModal({ open: true, asset })} aria-label="Edit"><FeatherIcon icon="edit-2" size={14} /></button>
                            </Tooltip>
                            <Tooltip title="Remove" arrow>
                              <button type="button" className="ts-icon-btn ts-icon-btn--danger" onClick={() => removeAsset(asset)} aria-label="Remove"><FeatherIcon icon="trash-2" size={14} /></button>
                            </Tooltip>
                          </>
                        )}
                      </div>
                    </td>
                  );
                  return (
                    <Fragment key={asset._id}>
                      {isVehicle ? (
                        <tr className={`ts-row ts-row--${asset.rowStatus}`}>
                          <td><div className="ts-primary">{asset.vehicle?.plateNumber}</div>{asset.notes && <div className="ts-secondary">{asset.notes}</div>}</td>
                          <td>{asset.vehicle?.vehicleType || '—'}</td>
                          <td>{asset.vehicle?.model || '—'}</td>
                          <td>
                            <button type="button" className="st-km" onClick={() => editKm(asset)} disabled={!perms.canRecord} aria-label={`Update km, now ${fmtKm(asset.vehicle?.currentKm)}`}>
                              {fmtKm(asset.vehicle?.currentKm)} {perms.canRecord && <FeatherIcon icon="edit-3" size={11} />}
                            </button>
                          </td>
                          {services}
                          <td>
                            {done ? (
                              <>
                                <div className="ts-primary">{done.title}</div>
                                <div className="ts-secondary">{fmtDate(done.completion?.completedDate)}{done.completion?.km != null ? ` · ${fmtKm(done.completion.km)} km` : ''}</div>
                              </>
                            ) : <span className="ts-secondary">—</span>}
                          </td>
                          <td><FileButtons files={done?.completion?.files} /></td>
                          {actions}
                        </tr>
                      ) : (
                        <tr className={`ts-row ts-row--${asset.rowStatus}`}>
                          {!singleBuilding && (
                            <td><div className="ts-primary">{asset.equipment?.buildingName}</div>{asset.propertyName && <div className="ts-secondary">{asset.propertyName}</div>}</td>
                          )}
                          <td className="ts-primary">{asset.equipment?.equipmentType}</td>
                          <td>{asset.equipment?.name || '—'}</td>
                          {services}
                          <td>
                            {done ? (
                              <>
                                <div className="ts-primary">{fmtDate(done.completion?.completedDate)}</div>
                                <div className="ts-secondary">{done.title}</div>
                              </>
                            ) : <span className="ts-secondary">—</span>}
                          </td>
                          <td style={{ maxWidth: 260, whiteSpace: 'pre-wrap' }}>{done?.completion?.notes || asset.notes || <span className="ts-secondary">—</span>}</td>
                          <td><FileButtons files={done?.completion?.files} /></td>
                          {actions}
                        </tr>
                      )}
                      {open && (
                        <tr>
                          <td colSpan={columnCount} style={{ padding: 0 }}>
                            <History asset={asset} isVehicle={isVehicle} canManage={perms.canManage} canRecord={perms.canRecord} refreshKey={refreshKey} onChanged={load} />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Paper>

        {canSeeReminders && showReminders && (
          <div style={{ marginTop: 20 }}>
            <AutomatedEmailsSettings sections={['serviceReminders']} readOnly={role !== 'company'} compact />
          </div>
        )}

        <AssetModal
          isOpen={assetModal.open}
          onClose={() => setAssetModal({ open: false, asset: null })}
          kind={kind}
          asset={assetModal.asset}
          properties={properties}
          defaults={{ propertyId: filters.property_id, buildingName: filters.building_name }}
          typeSuggestions={suggestions.types}
          onSaved={changed}
        />
        <TaskModal
          isOpen={taskModal.open}
          onClose={() => setTaskModal({ open: false, asset: null, task: null })}
          kind={kind}
          asset={taskModal.asset}
          task={taskModal.task}
          titleSuggestions={suggestions.titles}
          onSaved={changed}
        />
        <CompleteModal
          isOpen={completeModal.open}
          onClose={() => setCompleteModal({ open: false, asset: null, task: null })}
          kind={kind}
          asset={completeModal.asset}
          task={completeModal.task}
          onSaved={changed}
        />
      </div>
    </div>
  );
};

export const VehicleServices = () => <ServiceTracker kind="vehicle" />;
export const EquipmentServices = () => <ServiceTracker kind="equipment" />;
